import React from "react";
import styled from "styled-components";

const ProjectRow = styled.div`
  display: flex;
  gap: 3rem;
  align-items: center;
  width: 100%;
  flex-direction: ${props => props.$isReversed ? "row-reverse" : "row"};
  background: #1e1e1e;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);

  @media (max-width: 960px) {
    flex-direction: column;
    gap: 2rem;
    padding: 16px;
  }
`;

const ImageContainer = styled.div`
  flex: 1.2;
  position: relative;
  border: 1px solid #333;
  border-radius: 6px;
  overflow: hidden;
  background: #1a1a1a;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  width: 100%;
`;

const Image = styled.img`
  width: 100%;
  height: 320px;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.03);
  }

  @media (max-width: 640px) {
    height: 240px;
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  text-align: left;
`;

const ProjectTitle = styled.h3`
  font-size: 1.8rem;
  color: ${({ theme }) => theme.white};
  margin-bottom: 0.5rem;
  font-weight: 700;
  letter-spacing: -0.02em;

  @media (max-width: 640px) {
    font-size: 1.5rem;
  }
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 1.5rem;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
  color: #a0a0a0;
`;

const TechTag = styled.span`
  color: ${({ theme }) => theme.text_primary};
  white-space: nowrap;
`;

const Separator = styled.span`
  color: ${({ theme }) => theme.primary};
  margin: 0 0.6rem;
  font-weight: bold;
`;

const ProjectDesc = styled.p`
  color: ${({ theme }) => theme.text_secondary};
  font-size: 1.05rem;
  margin-bottom: 2rem;
  line-height: 1.7;
`;

const SourceButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1.2rem;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  font-family: inherit;
  align-self: flex-start;
  background-color: transparent;
  color: ${({ theme }) => theme.text_primary};
  border: 1px solid #444;
  gap: 0.5rem;

  &:hover {
    border-color: ${({ theme }) => theme.text_primary};
    background-color: rgba(255, 255, 255, 0.05);
    transform: translateY(-2px);
  }
`;

const ProjectCard = ({ project, index }) => {
  const isReversed = index % 2 !== 0;

  return (
    <ProjectRow $isReversed={isReversed}>
      <ImageContainer>
        <Image src={project.image} alt={project.title} />
      </ImageContainer>
      
      <ContentContainer>
        <ProjectTitle>{project.title}</ProjectTitle>
        
        <TechStack>
          {project.tags?.map((tag, tagIdx) => (
            <React.Fragment key={tagIdx}>
              {tagIdx > 0 && <Separator>{"//"}</Separator>}
              <TechTag>{tag}</TechTag>
            </React.Fragment>
          ))}
        </TechStack>

        <ProjectDesc>{project.description}</ProjectDesc>

        {project.github && (
          <SourceButton href={project.github} target="_blank">
            <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16">
              <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
            </svg>
            View Source
          </SourceButton>
        )}
      </ContentContainer>
    </ProjectRow>
  );
};

export default ProjectCard;
